// 3rd party libraries
import React from 'react';
import styled from 'styled-components';
import { themeColors } from 'themes';

const Container = styled.button`
  all: unset;
  display: flex;
  justify-conent: flex-end;
  gap: 1rem;
`;
export type Variant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'buyNow'
  | 'danger'
  | 'warning'
  | 'link'
  | 'inline-link'
  | 'secondary-danger'
  | 'emergency'
  | 'hover-blue'
  | 'filled-primary'
  | 'filled-generic' // Added this
  | 'outline-blue'
  | 'outline-generic' // Added this
  | 'outline-danger'
  | 'constant'
  | 'upload'
  | 'thin'
  | 'secondary-light'
  | 'no-shadow'
  | 'text';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?: Variant;
  size?: 'small' | 'normal';
  disabled?: boolean;
  bgColor?: string;
  arrow?: boolean;
  width?: string;
  height?: string;
  borderColor?: string;
  borderRadius?: string;
  icon?: React.ReactNode;
  marginLeft?: string;
}

export const Button = styled.button<Props>`
  display: ${({ variant }) => {
    switch (variant) {
      case 'inline-link':
        return 'inline';
      default:
        return 'flex';
    }
  }};
  justify-content: ${({ arrow }) => (arrow ? 'space-between' : 'center')};

  width: ${({ variant, width }) => {
    if (width) {
      return width;
    }
    switch (variant) {
      case 'filled-primary':
      case 'filled-generic':
      case 'outline-blue':
      case 'outline-generic':
      case 'outline-danger':
        return 'auto';
      default:
        return '100%';
    }
  }};

  height: ${({ height }) => height ?? 'auto'};
  white-space: nowrap;
  padding: ${({ size, variant }) => {
    switch (variant) {
      case 'inline-link':
        return 0;
      case 'filled-primary':
      case 'filled-generic':
      case 'outline-blue':
      case 'outline-generic':
      case 'outline-danger':
        if (size === 'small') {
          return '0.25rem 2rem';
        } else {
          return '0.75rem 2rem';
        }
    }
    switch (size) {
      case 'small':
        return '0.5rem 1.375rem';
      default:
        return '0.75rem 1.375rem';
    }
  }};
  background-color: ${({ variant, theme, bgColor }) => {
    if (bgColor) {
      return bgColor;
    }
    switch (variant) {
      case 'primary':
      case 'hover-blue':
        return theme.colors.buttons.primary.fill;
      case 'buyNow':
        return '#e5edff';
      case 'secondary':
      case 'secondary-light':
        return theme.colors.primaryBlue4;
      case 'warning':
        return theme.colors.buttons.warning.fill;
      case 'danger':
        return theme.colors.buttons.danger.fill;
      case 'emergency':
        return theme.colors.darkRed;
      case 'filled-primary':
      case 'filled-generic':
        return theme.colors.buttons.filledPrimary.fill;
      case 'outline-blue':
      case 'outline-generic':
        return theme.colors.buttons.outlineBlue.fill;
      case 'outline-danger':
        return theme.colors.buttons.outlineDanger.fill;
      case 'link':
      case 'inline-link':
        return 'transparent';
      case 'text':
        return 'unset';
      default:
        return 'white';
    }
  }};
  border: 0.0625rem solid;
  border-color: ${({ borderColor, variant, theme }) => {
    if (borderColor) return borderColor;
    switch (variant) {
      case 'primary':
        return theme.colors.buttons.primary.fill;
      case 'outline-blue':
        return theme.colors.buttons.outlineBlue.border;
      case 'outline-danger':
        return theme.colors.buttons.outlineDanger.border;
      case 'buyNow':
      case 'secondary':
        return '#e5edff';
      case 'link':
      case 'inline-link':
        return 'transparent';
      case 'thin':
        return theme.colors.lightGrey3;
      default:
        return '#D1D5DB';
    }
  }};

  border-radius: ${({ variant, borderRadius }) => {
    switch (variant) {
      case 'filled-primary':
      case 'filled-generic':
      case 'outline-blue':
      case 'outline-generic':
      case 'outline-danger':
        return borderRadius ? borderRadius : '3rem';
      default:
        return borderRadius ? borderRadius : '0.625rem';
    }
  }};
  font-size: 0.875rem;
  font-weight: ${({ variant }) => {
    switch (variant) {
      case 'link':
      case 'inline-link':
      case 'thin':
        return 'normal';
      default:
        return 600;
    }
  }};
  line-height: ${({ variant }) => {
    switch (variant) {
      case 'filled-primary':
      case 'filled-generic':
      case 'outline-blue':
      case 'outline-generic':
      case 'outline-danger':
        return '1.7';
      default:
        return '1rem';
    }
  }};
  text-align: ${({ variant }) => {
    switch (variant) {
      case 'thin':
        return 'left';
      default:
        return 'center';
    }
  }};

  transition: color 0.5s, background-color 0.5s, border-color 0.5s;

  &:hover,
  &:focus,
  &:active {
    background-color: ${({ variant, theme, bgColor }) => {
      switch (variant) {
        case 'primary':
          return '#2F446C';
        case 'buyNow':
        case 'secondary':
          return '#e5edff';
        case 'inline-link':
          return 'transparent';
        case 'link':
          // theme.colors.mediumBlue but with opacity
          return 'rgba(10, 112, 255, 0.1)';
        case 'danger':
          return theme.colors.darkRed;
        case 'emergency':
          return theme.colors.darkRed;
        case 'secondary-danger':
          return theme.colors.lightRed;
        case 'upload':
          return '#004577';
        case 'hover-blue':
          return theme?.colors?.darkBlue1;
        case 'constant':
          return bgColor;
        case 'text':
          return 'transparent';
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.hover;
        case 'filled-generic':
        case 'outline-generic':
          return 'none'; // No change on hover
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.fill;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.fill;
        default:
          return theme.colors.lightGrey3;
      }
    }};
    border-color: ${({ borderColor, variant, theme }) => {
      if (borderColor) return borderColor;
      switch (variant) {
        case 'primary':
          return '#2F446C';
        case 'buyNow':
        case 'secondary':
          return '#584ccf';
        case 'link':
        case 'inline-link':
        case 'secondary-light':
        case 'secondary-danger':
          return 'transparent';
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.fill;
        case 'filled-generic':
        case 'outline-generic':
          return 'none'; // No change on hover
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.border;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.border;
        default:
          return '#C8CED4';
      }
    }};
  }

  &:focus,
  &:active {
    background-color: ${({ variant, theme }) => {
      switch (variant) {
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.activeBg;
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.activeBg;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.activeBg;
      }
    }};
    color: ${({ variant, theme }) => {
      switch (variant) {
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.text;
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.fill;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.fill;
      }
    }};
  }

  &:focus-visible {
    // outline: 2px solid;
    // outline-offset: 3px;
    outline-color: ${({ variant, theme }) => {
      switch (variant) {
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.fill;
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.border;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.border;
      }
    }};

    background-color: ${({ variant, theme }) => {
      switch (variant) {
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.hover;
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.fill;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.fill;
      }
    }};
    color: ${({ variant, theme }) => {
      switch (variant) {
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.text;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.text;
      }
    }};
    border: 0.0625rem solid;
    border-color: ${({ variant, theme }) => {
      switch (variant) {
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.border;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.border;
      }
    }};
  }

  &:focus-visible:active {
    background-color: ${({ variant, theme }) => {
      switch (variant) {
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.activeBg;
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.activeBg;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.activeBg;
      }
    }};
    color: ${({ variant, theme }) => {
      switch (variant) {
        case 'filled-primary':
          return theme.colors.buttons.filledPrimary.text;
        case 'outline-blue':
          return theme.colors.buttons.outlineBlue.fill;
        case 'outline-danger':
          return theme.colors.buttons.outlineDanger.fill;
      }
    }};
  }

  &:disabled {
    color: ${({ variant }) => {
      switch (variant) {
        case 'hover-blue':
        case 'outline-blue':
        case 'outline-generic':
        case 'outline-danger':
        case 'filled-primary':
        case 'filled-generic':
          return themeColors.white;
        default:
          return '#a7a7a7';
      }
    }};
    background-color: ${({ variant }) => {
      switch (variant) {
        case 'hover-blue':
        case 'outline-blue':
        case 'outline-generic':
        case 'outline-danger':
        case 'filled-primary':
        case 'filled-generic':
          return themeColors.disabledColor;
        case 'text':
          return 'unset';
        default:
          return '#f0f0f0';
      }
    }};
    border-color: #f0f0f0;
  }
  all: ${({ variant }) => (variant === 'text' ? 'unset' : '')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  color: ${({ variant, color, theme }) => {
    if (color) {
      return color;
    }
    switch (variant) {
      case 'primary':
      case 'danger':
      case 'warning':
      case 'hover-blue':
        return 'white';
      case 'buyNow':
        return '#584ccf';
      case 'secondary':
        return theme.colors.darkGrey;
      case 'link':
      case 'inline-link':
        return color ? color : theme.colors.mediumBlue;
      case 'text':
        return color;
      case 'filled-primary':
      case 'filled-generic':
        return color ? color : theme.colors.buttons.filledPrimary.text;
      case 'outline-blue':
      case 'outline-generic':
        return color ? color : theme.colors.buttons.outlineBlue.text;
      case 'outline-danger':
        return color ? color : theme.colors.buttons.outlineDanger.text;
      default:
        return '#353B4B';
    }
  }};
  svg {
    margin-left: ${({ marginLeft }) => marginLeft || '0.75rem'};
  }
`;

const chevron = (
  <svg width="6" height="8" viewBox="0 0 6 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.35449 2.74871C5.921 3.12027 5.96187 3.93575 5.43535 4.36208L1.62929 7.44389C0.975488 7.97328 5.67359e-08 7.50797 9.47617e-08 6.66672L3.469e-07 1.08863C3.82849e-07 0.293331 0.883412 -0.183735 1.54843 0.252433L5.35449 2.74871Z"
      fill="currentColor"
    />
  </svg>
);

const chevronThin = (
  <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.1417 0L0 1.175L3.7085 5L0 8.825L1.1417 10L6 5L1.1417 0Z" fill="currentColor" />
  </svg>
);

export const ButtonContainer = ({
  children
}: {
  children: JSX.Element | JSX.Element[];
}): JSX.Element => {
  return <Container>{children}</Container>;
};

const ButtonComponent = React.forwardRef<HTMLButtonElement, Props>(
  ({ children, arrow, variant, icon, ...rest }: Props, ref): JSX.Element => (
    <Button arrow={arrow} variant={variant} ref={ref} {...rest}>
      {icon ? icon : null}
      {children}
      {arrow ? (variant === 'thin' ? chevronThin : chevron) : null}
    </Button>
  )
);

ButtonComponent.displayName = 'ButtonComponent';

export default ButtonComponent;
